import React from 'react';
import { Upload, Sparkles, FileText, Target } from 'lucide-react';

const ExplorationNavigationCards = ({ 
  onNavigateToDocuments,
  onNavigateToSyntheses, 
  onNavigateToNotes,
  onNavigateToQuiz,
  stats = {}
}) => {
  // Fonction pour gérer le clic avec scroll automatique
  const handleCardClick = (onClick) => {
    // Exécuter la fonction de navigation
    if (onClick) onClick();
    
    // Scroll automatique vers le haut de la page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const cards = [
    {
      id: 'documents',
      title: "Construire ma base de connaissance",
      description: "Accédez directement à l'onglet Documents pour télécharger et organiser vos ressources",
      icon: <Upload className="h-8 w-8" />,
      onClick: onNavigateToDocuments,
      // Couleurs harmonisées avec l'onglet Documents (bleu)
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      hoverBg: "hover:bg-blue-100",
      hoverBorder: "hover:border-blue-300",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      count: stats.documentsCount
    },
    {
      id: 'syntheses',
      title: "Retrouver vos synthèses d'inspiration",
      description: "Consultez vos synthèses d'inspiration et accédez au détail de vos sources",
      icon: <Sparkles className="h-8 w-8" />,
      onClick: onNavigateToSyntheses,
      // Couleurs harmonisées avec l'onglet Synthèses (vert clair)
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      hoverBg: "hover:bg-green-100",
      hoverBorder: "hover:border-green-300",
      iconBg: "bg-green-100",
      iconColor: "text-green-600",
      count: stats.synthesesCount
    },
    {
      id: 'concepts',
      title: "Comprendre les concepts essentiels",
      description: "Explorez les concepts clés identifiés automatiquement dans vos documents",
      icon: <Target className="h-8 w-8" />,
      onClick: onNavigateToQuiz, // Navigation vers l'onglet Quiz/Concepts
      // Couleurs harmonisées avec l'onglet Concepts (orange)
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
      hoverBg: "hover:bg-orange-100",
      hoverBorder: "hover:border-orange-300",
      iconBg: "bg-orange-100",
      iconColor: "text-orange-600",
      count: stats.conceptsCount
    },
    {
      id: 'notes',
      title: "Organiser mes découvertes",
      description: "Sauvegardez et organisez vos recherches importantes dans vos notes personnelles",
      icon: <FileText className="h-8 w-8" />,
      onClick: onNavigateToNotes,
      // Couleurs harmonisées avec l'onglet Notes (violet)
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      hoverBg: "hover:bg-purple-100",
      hoverBorder: "hover:border-purple-300",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
      count: stats.notesCount
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {cards.map((card) => (
        <button
          key={card.id}
          onClick={() => handleCardClick(card.onClick)}
          className={`
            aspect-square min-h-[200px] 
            ${card.bgColor} ${card.borderColor} ${card.hoverBg} ${card.hoverBorder}
            rounded-2xl border-2 
            transition-all duration-300 
            hover:shadow-lg hover:scale-105
            flex flex-col items-center justify-center p-6
            text-left cursor-pointer
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
          `}
        >
          {/* Icône centrée en haut */}
          <div className={`${card.iconBg} ${card.iconColor} p-3 rounded-xl mb-4`}>
            {card.icon}
          </div>

          {/* Contenu centré */}
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <h3 className="font-semibold text-gray-800 mb-2 text-base leading-tight">
              {card.title}
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-3">
              {card.description}
            </p>
            
            {/* Badge de compteur */}
            {card.count !== undefined && card.count > 0 && (
              <div className={`${card.iconBg} ${card.iconColor} rounded-full px-3 py-1 text-xs font-semibold`}>
                {card.count} disponible{card.count > 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* Indicateur de clic subtil */}
          <div className="mt-2 text-xs text-gray-500 font-medium">
            Cliquez pour accéder
          </div>
        </button>
      ))}
    </div>
  );
};

export default ExplorationNavigationCards;
